import { Num } from "@/components/Num";
import { useEffect, useState } from "react";
import { TrendingUp, Save, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errorText } from "@/lib/api";
import { MONTHS, fmtPct } from "@/lib/format";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const METHODS = [
  { id: "budget", label: "Remaining months = approved budget" },
  { id: "budget_adj", label: "Budget scaled by YTD performance ratio" },
  { id: "run_rate", label: "Run-rate (avg of last 3 actual months)" },
  { id: "trend", label: "Linear trend of actual months" },
];

export const ReforecastPanel = ({ projectId, refreshKey, onVersionSaved }) => {
  const [method, setMethod] = useState("budget_adj");
  const [adj, setAdj] = useState(0);
  const [data, setData] = useState(null);
  const { can } = useAuth();
  const [busy, setBusy] = useState("");
  const saveVersion = async () => {
    setBusy("save");
    try {
      const { data: v } = await api.post(`/projects/${projectId}/reforecast/save-version`, null, { params: { method, adj_pct: adj } });
      toast.success(`Saved "${v.name}" — see Versions & Scenarios`);
      onVersionSaved?.(v);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  const exportXlsx = async () => {
    setBusy("export");
    try {
      await downloadFile(`/projects/${projectId}/reforecast/export?method=${method}&adj_pct=${adj}`, `budget-vs-reforecast-${method}.xlsx`);
      toast.success("Budget vs Reforecast workbook downloaded");
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  useEffect(() => {
    api.get(`/projects/${projectId}/reforecast`, { params: { method, adj_pct: adj } }).then((r) => setData(r.data)).catch((e) => toast.error(errorText(e)));
  }, [projectId, method, adj, refreshKey]);
  if (!data) return null;
  const k = data.kpis || {};
  return (
    <div className="space-y-4" data-testid="reforecast-panel">
      <div className="bg-slate-900 text-white rounded-lg p-5 grain relative overflow-hidden">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-emerald-300 inline-flex items-center gap-1"><TrendingUp size={13} /> Reforecast mode</p>
            <h3 className="font-heading text-xl font-semibold mt-1">Full-year {data.year} reforecast = actual YTD ({data.last_month} month{data.last_month === 1 ? "" : "s"}) + revised remaining months</h3>
            <p className="text-xs text-slate-300 mt-1">Compared against the {data.reference} budget. Remaining months use the selected method; the adjustment % scales the remaining non-ratio months.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="reforecast-method-select" className="h-9 rounded-md bg-white text-slate-900 px-3 text-xs">
              {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <label className="text-xs text-slate-300 inline-flex items-center gap-1">Adj <input type="number" step="0.5" value={adj} onChange={(e) => setAdj(Number(e.target.value) || 0)} data-testid="reforecast-adj-input" className="w-16 h-9 rounded-md bg-white text-slate-900 px-2 font-mono text-xs" /> %</label>
            {can("edit") && <Button size="sm" variant="outline" onClick={saveVersion} disabled={busy !== ""} className="h-9 border-emerald-400/60 bg-transparent text-emerald-200 hover:bg-white/10 hover:text-white" data-testid="reforecast-save-version-button"><Save size={13} className="mr-1" /> {busy === "save" ? "Saving..." : "Save as version"}</Button>}
            {can("export") && <Button size="sm" onClick={exportXlsx} disabled={busy !== ""} className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="reforecast-export-button"><FileSpreadsheet size={13} className="mr-1" /> {busy === "export" ? "Preparing..." : "Budget vs Reforecast Excel"}</Button>}
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {["revenue", "gop", "payroll", "ebitda"].map((key) => k[key] && (
            <div key={key} className="rounded-lg bg-white/10 backdrop-blur p-3" data-testid={`reforecast-kpi-${key}`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-300">{k[key].label}</p>
              <p className="font-heading text-2xl font-bold tabular mt-1"><Num v={k[key].reforecast_total} compact /></p>
              <p className="text-xs text-slate-300">Budget <Num v={k[key].budget_total} compact /> · <span className={`font-semibold ${(k[key].var || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtPct(k[key].var_pct)}</span></p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="reforecast-table-container">
        <Tabs defaultValue={slug(data.sheets[0].name)}>
          <TabsList className="flex-wrap h-auto justify-start bg-slate-100 m-4 mb-0">
            {data.sheets.map((s) => <TabsTrigger key={s.name} value={slug(s.name)} data-testid={`reforecast-tab-${slug(s.name)}`} className="text-xs data-[state=active]:bg-slate-900 data-[state=active]:text-white">{s.name}</TabsTrigger>)}
          </TabsList>
          {data.sheets.map((s) => (
            <TabsContent key={s.name} value={slug(s.name)} className="mt-3">
              <div className="overflow-auto max-h-[60vh]">
                <table className="budget-table min-w-full text-xs">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="sticky-col !bg-slate-900 text-left px-3 py-2 min-w-[240px] text-[11px] uppercase tracking-wider">Description</th>
                      {MONTHS.map((m, i) => <th key={m} className={`px-2 py-2 text-right text-[11px] ${i < data.last_month ? "text-emerald-200" : "text-amber-300"}`}>{m}{i < data.last_month ? " A" : " F"}</th>)}
                      <th className="px-2 py-2 text-right text-[11px] bg-emerald-900">Reforecast FY</th>
                      <th className="px-2 py-2 text-right text-[11px] bg-emerald-900">Budget FY</th>
                      <th className="px-2 py-2 text-right text-[11px] bg-emerald-900">Var</th>
                      <th className="px-2 py-2 text-right text-[11px] bg-emerald-900">Var %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((l) => l.row_type === "header" ? (
                      <tr key={l.id} className="bg-slate-50"><td className="sticky-col !bg-slate-50 px-3 py-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">{l.label}</td><td colSpan={16} className="bg-slate-50" /></tr>
                    ) : (
                      <tr key={l.id} className={`border-b border-slate-100 hover:bg-slate-50/80 ${l.row_type !== "line" ? "font-semibold bg-slate-50/60" : ""}`} data-testid={`reforecast-row-${l.id}`}>
                        <td className="sticky-col px-3 py-1.5 text-slate-900" style={{ paddingLeft: `${12 + Math.min(l.indent, 8) * 4}px` }}>{l.computed_usali ? l.label.replace(/ \(computed — USALI\)/, "") : l.label}{l.computed_usali && <span className="ml-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 text-[9px] uppercase tracking-wider" data-testid={`usali-computed-${l.id}`}>USALI</span>}</td>
                        {MONTHS.map((m, i) => <td key={m} className={`px-2 py-1.5 text-right font-mono ${i < data.last_month ? "text-slate-800" : "text-amber-800 italic bg-amber-50/40"}`}><Num v={l.reforecast[i]} line={l} /></td>)}
                        <td className="px-2 py-1.5 text-right font-mono font-semibold bg-emerald-50/40"><Num v={l.reforecast_total} line={l} /></td>
                        <td className="px-2 py-1.5 text-right font-mono bg-emerald-50/40"><Num v={l.budget_total} line={l} /></td>
                        <td className={`px-2 py-1.5 text-right font-mono bg-emerald-50/40 ${(l.var || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}><Num v={l.var} line={l} /></td>
                        <td className={`px-2 py-1.5 text-right font-mono bg-emerald-50/40 ${(l.var_pct || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtPct(l.var_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};
